import { useCallback, useMemo, useState } from 'react'
import type {
  ApprovalOption,
  ApprovalType,
  ComparisonTab,
  GuidanceType,
} from '../components/ComparisonTabs'
import { GridViewType } from '../components/GridViewToggle'
import { PL_GUIDANCE_TYPES } from '../constants'
import {
  assignColorSlot,
  freeColorSlot,
  isColorLimitReached,
  resolveColorMap,
  type ColumnKey,
  type DynamicColorMap,
} from '../utils/dynamicColorMap'

interface InitialSelections {
  initialTabs?: ComparisonTab[]
  initialGuidanceSelections?: GuidanceType[]
}

interface UseDataPointSelectionsResult {
  currentActiveTabs: ComparisonTab[]
  currentApprovalSelections: ApprovalType[]
  currentGuidanceSelections: GuidanceType[]
  currentColorMap: ReturnType<typeof resolveColorMap>
  currentIsLimitReached: boolean
  plActiveTabs: ComparisonTab[]
  pnActiveTabs: ComparisonTab[]
  sanitizedPlApprovalSelections: ApprovalType[]
  sanitizedPnApprovalSelections: ApprovalType[]
  plGuidanceSelections: GuidanceType[]
  pnGuidanceSelections: GuidanceType[]
  plResolvedColorMap: ReturnType<typeof resolveColorMap>
  pnResolvedColorMap: ReturnType<typeof resolveColorMap>
  isSelectionsEmpty: boolean
  handleTabsChange: (tabs: ComparisonTab[]) => void
  handleGuidanceTypesChange: (types: GuidanceType[]) => void
  handleApprovalTypesChange: (types: ApprovalType[]) => void
  replaceGuidanceSelections: (types: GuidanceType[]) => void
  restorePersistedState: (payload: { tabs: ComparisonTab[]; guidance: GuidanceType[] }) => void
  resetSelections: () => void
}

const updateColorMap = (
  colorMap: DynamicColorMap,
  added: ColumnKey | undefined,
  removed: ColumnKey | undefined
): DynamicColorMap | null => {
  let updated = colorMap
  if (added) {
    updated = assignColorSlot(updated, added)
    if (updated[added] === undefined) return null
  }
  if (removed) {
    updated = freeColorSlot(updated, removed)
  }
  return updated
}

const buildInitialColorMap = (
  tabs: ComparisonTab[],
  guidanceSelections: GuidanceType[]
): DynamicColorMap => {
  let colorMap: DynamicColorMap = {}
  const keys: ColumnKey[] = [
    ...(tabs.includes('requestedPrice') ? (['requestedPrice'] as ColumnKey[]) : []),
    ...(guidanceSelections as ColumnKey[]),
  ]
  for (const key of keys) {
    colorMap = assignColorSlot(colorMap, key)
  }
  return colorMap
}

export const useDataPointSelections = (
  gridView: GridViewType,
  approvalOptions: ApprovalOption[] | undefined,
  { initialTabs = [], initialGuidanceSelections = [] }: InitialSelections = {}
): UseDataPointSelectionsResult => {
  const [plActiveTabs, setPlActiveTabs] = useState<ComparisonTab[]>(
    gridView === 'PL' ? initialTabs : []
  )
  const [pnActiveTabs, setPnActiveTabs] = useState<ComparisonTab[]>(
    gridView === 'PN' ? initialTabs : []
  )
  const [plApprovalSelections, setPlApprovalSelections] = useState<ApprovalType[]>([])
  const [pnApprovalSelections, setPnApprovalSelections] = useState<ApprovalType[]>([])
  const [plGuidanceSelections, setPlGuidanceSelections] = useState<GuidanceType[]>(
    gridView === 'PL' ? initialGuidanceSelections : []
  )
  const [pnGuidanceSelections, setPnGuidanceSelections] = useState<GuidanceType[]>(
    gridView === 'PN' ? initialGuidanceSelections : []
  )
  const [plColorMap, setPlColorMap] = useState<DynamicColorMap>(() =>
    gridView === 'PL' ? buildInitialColorMap(initialTabs, initialGuidanceSelections) : {}
  )
  const [pnColorMap, setPnColorMap] = useState<DynamicColorMap>(() =>
    gridView === 'PN' ? buildInitialColorMap(initialTabs, initialGuidanceSelections) : {}
  )

  const allowedApprovalIds = useMemo(() => approvalOptions?.map((o) => o.id), [approvalOptions])

  const replaceGuidanceSelections = useCallback(
    (types: GuidanceType[]) => {
      const allowedGuidanceTypes = gridView === 'PL' ? PL_GUIDANCE_TYPES : undefined
      const sanitizedTypes = allowedGuidanceTypes
        ? types.filter((type) => allowedGuidanceTypes.includes(type))
        : types

      const currentTabs = gridView === 'PL' ? plActiveTabs : pnActiveTabs
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlGuidanceSelections : setPnGuidanceSelections

      // Rebuild color map from scratch preserving the requestedPrice tab slot
      const rebuiltColorMap = buildInitialColorMap(currentTabs, sanitizedTypes)

      setColorMap(rebuiltColorMap)
      setSelections(sanitizedTypes)
    },
    [gridView, plActiveTabs, pnActiveTabs]
  )

  const sanitizeApprovalSelections = useCallback(
    (types: ApprovalType[]): ApprovalType[] =>
      allowedApprovalIds ? types.filter((type) => allowedApprovalIds.includes(type)) : types,
    [allowedApprovalIds]
  )

  const sanitizedPlApprovalSelections = useMemo(
    () => sanitizeApprovalSelections(plApprovalSelections),
    [plApprovalSelections, sanitizeApprovalSelections]
  )

  const sanitizedPnApprovalSelections = useMemo(
    () => sanitizeApprovalSelections(pnApprovalSelections),
    [pnApprovalSelections, sanitizeApprovalSelections]
  )

  const currentActiveTabs = gridView === 'PL' ? plActiveTabs : pnActiveTabs
  const currentApprovalSelections =
    gridView === 'PL' ? sanitizedPlApprovalSelections : sanitizedPnApprovalSelections
  const currentGuidanceSelections = gridView === 'PL' ? plGuidanceSelections : pnGuidanceSelections

  const currentColorMap = useMemo(
    () => resolveColorMap(gridView === 'PL' ? plColorMap : pnColorMap),
    [gridView, plColorMap, pnColorMap]
  )

  const currentIsLimitReached = isColorLimitReached(gridView === 'PL' ? plColorMap : pnColorMap)

  const plResolvedColorMap = useMemo(() => resolveColorMap(plColorMap), [plColorMap])
  const pnResolvedColorMap = useMemo(() => resolveColorMap(pnColorMap), [pnColorMap])

  const isSelectionsEmpty =
    plActiveTabs.length === 0 &&
    pnActiveTabs.length === 0 &&
    sanitizedPlApprovalSelections.length === 0 &&
    sanitizedPnApprovalSelections.length === 0 &&
    plGuidanceSelections.length === 0 &&
    pnGuidanceSelections.length === 0 &&
    Object.keys(plColorMap).length === 0 &&
    Object.keys(pnColorMap).length === 0

  const handleTabsChange = useCallback(
    (tabs: ComparisonTab[]) => {
      const prevTabs = gridView === 'PL' ? plActiveTabs : pnActiveTabs
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setTabs = gridView === 'PL' ? setPlActiveTabs : setPnActiveTabs

      const added = tabs.find((t) => t === 'requestedPrice' && !prevTabs.includes(t)) as
        | ColumnKey
        | undefined
      const removed = prevTabs.find((t) => t === 'requestedPrice' && !tabs.includes(t)) as
        | ColumnKey
        | undefined

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setTabs(tabs)
    },
    [gridView, plActiveTabs, pnActiveTabs, plColorMap, pnColorMap]
  )

  const handleGuidanceTypesChange = useCallback(
    (types: GuidanceType[]) => {
      const allowedGuidanceTypes = gridView === 'PL' ? PL_GUIDANCE_TYPES : undefined
      const sanitizedTypes = allowedGuidanceTypes
        ? types.filter((type) => allowedGuidanceTypes.includes(type))
        : types

      const prevTypes = gridView === 'PL' ? plGuidanceSelections : pnGuidanceSelections
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlGuidanceSelections : setPnGuidanceSelections

      const added = sanitizedTypes.find((t) => !prevTypes.includes(t))
      const removed = prevTypes.find((t) => !sanitizedTypes.includes(t))

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setSelections(sanitizedTypes)
    },
    [gridView, plGuidanceSelections, pnGuidanceSelections, plColorMap, pnColorMap]
  )

  const handleApprovalTypesChange = useCallback(
    (types: ApprovalType[]) => {
      const sanitizedTypes = sanitizeApprovalSelections(types)

      const prevTypes =
        gridView === 'PL' ? sanitizedPlApprovalSelections : sanitizedPnApprovalSelections
      const colorMap = gridView === 'PL' ? plColorMap : pnColorMap
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlApprovalSelections : setPnApprovalSelections

      const added = sanitizedTypes.find((t) => !prevTypes.includes(t))
      const removed = prevTypes.find((t) => !sanitizedTypes.includes(t))

      const updatedColorMap = updateColorMap(colorMap, added, removed)
      if (updatedColorMap === null) return

      setColorMap(updatedColorMap)
      setSelections(sanitizedTypes)
    },
    [
      gridView,
      sanitizeApprovalSelections,
      sanitizedPlApprovalSelections,
      sanitizedPnApprovalSelections,
      plColorMap,
      pnColorMap,
    ]
  )

  const restorePersistedState = useCallback(
    ({ tabs, guidance }: { tabs: ComparisonTab[]; guidance: GuidanceType[] }) => {
      const setTabs = gridView === 'PL' ? setPlActiveTabs : setPnActiveTabs
      const setColorMap = gridView === 'PL' ? setPlColorMap : setPnColorMap
      const setSelections = gridView === 'PL' ? setPlGuidanceSelections : setPnGuidanceSelections
      // Sanitize guidance for PL (only floor/expert/typical are supported)
      const sanitizedGuidance =
        gridView === 'PL' ? guidance.filter((t) => PL_GUIDANCE_TYPES.includes(t)) : guidance
      // Build the full color map atomically from all keys so no slot is lost
      // due to stale closure state that would occur when calling handleTabsChange
      // and replaceGuidanceSelections sequentially (last setState wins on the map).
      setTabs(tabs)
      setSelections(sanitizedGuidance)
      setColorMap(buildInitialColorMap(tabs, sanitizedGuidance))
    },
    [gridView]
  )

  const resetSelections = useCallback(() => {
    setPlActiveTabs([])
    setPnActiveTabs([])
    setPlApprovalSelections([])
    setPnApprovalSelections([])
    setPlGuidanceSelections([])
    setPnGuidanceSelections([])
    setPlColorMap({})
    setPnColorMap({})
  }, [])

  return {
    currentActiveTabs,
    currentApprovalSelections,
    currentGuidanceSelections,
    currentColorMap,
    currentIsLimitReached,
    plActiveTabs,
    pnActiveTabs,
    sanitizedPlApprovalSelections,
    sanitizedPnApprovalSelections,
    plGuidanceSelections,
    pnGuidanceSelections,
    plResolvedColorMap,
    pnResolvedColorMap,
    isSelectionsEmpty,
    handleTabsChange,
    handleGuidanceTypesChange,
    handleApprovalTypesChange,
    replaceGuidanceSelections,
    restorePersistedState,
    resetSelections,
  }
}
